import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, Users, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';
import { groupVoteKeys } from '@/lib/queryKeys';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { createOpenGroupVote } from '@/services/groupVoteService';

interface EveningPlannerModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const BENEFITS = [
  'Proposez plusieurs activités à voter',
  "Vos invités n'ont pas besoin de compte",
  'Trouvez la date qui convient à tous',
];

const EveningPlannerModal = ({ isOpen, onClose }: EveningPlannerModalProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('Notre soirée de groupe');
  const [participants, setParticipants] = useState(4);
  const [isCreating, setIsCreating] = useState(false);

  const handleCreate = async () => {
    if (!user) return;
    const trimmedTitle = title.trim() || 'Notre soirée de groupe';
    setIsCreating(true);

    const result = await createOpenGroupVote({
      title: trimmedTitle,
      maxParticipants: participants,
      organizerId: user.id,
    });

    setIsCreating(false);

    if (!result.success || !result.groupVote) {
      toast.error(result.error ?? 'Erreur lors de la création du vote');
      return;
    }

    queryClient.invalidateQueries({ queryKey: groupVoteKeys.active(user.id) });
    queryClient.invalidateQueries({ queryKey: ['all-votes', user.id] });
    onClose();
    navigate(`/group-vote/${result.groupVote.id}`);
  };

  if (!user) {
    return (
      <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <div className="mx-auto mb-3 w-14 h-14 rounded-full bg-brand-primary/10 flex items-center justify-center text-3xl">
              🎉
            </div>
            <DialogTitle className="text-center text-[20px]">
              Planifie ta soirée en groupe
            </DialogTitle>
            <DialogDescription className="text-center text-[14px]">
              Crée un compte gratuit pour organiser ton vote. Tes amis n'auront pas besoin de s'inscrire.
            </DialogDescription>
          </DialogHeader>

          <ul className="space-y-2.5 my-1">
            {BENEFITS.map((benefit) => (
              <li key={benefit} className="flex items-start gap-2.5 text-[13px] text-brand-text">
                <span className="mt-0.5 w-5 h-5 rounded-full bg-brand-success flex items-center justify-center text-white text-[10px] font-bold shrink-0">
                  ✓
                </span>
                {benefit}
              </li>
            ))}
          </ul>

          <div className="flex flex-col gap-2.5 pt-2">
            <Button
              onClick={() => { onClose(); navigate('/auth?tab=signup'); }}
              className="w-full bg-brand-primary hover:bg-brand-primary-600 text-white"
            >
              Créer un compte gratuit
            </Button>
            <Button
              onClick={() => { onClose(); navigate('/auth?tab=login'); }}
              variant="outline"
              className="w-full"
            >
              J'ai déjà un compte
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <div className="mx-auto mb-3 w-14 h-14 rounded-full bg-brand-primary/10 flex items-center justify-center text-3xl">
            🎉
          </div>
          <DialogTitle className="text-center text-[20px]">
            Planifie ta soirée en groupe
          </DialogTitle>
          <DialogDescription className="text-center text-[14px]">
            Crée le vote, puis ajoute les activités et créneaux qui t'intéressent.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div className="space-y-1.5">
            <Label htmlFor="planner-title" className="flex items-center gap-1.5 text-[13px]">
              <CalendarDays size={13} className="text-brand-primary" />
              Nom de la soirée
            </Label>
            <Input
              id="planner-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Notre soirée de groupe"
              className="text-[15px]"
              onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="planner-participants" className="flex items-center gap-1.5 text-[13px]">
              <Users size={13} className="text-brand-primary" />
              Nombre de participants
            </Label>
            <Input
              id="planner-participants"
              type="number"
              min={2}
              max={50}
              value={participants}
              onChange={(e) => setParticipants(Math.max(2, parseInt(e.target.value) || 2))}
              className="text-[15px]"
            />
          </div>
        </div>

        <Button
          onClick={handleCreate}
          disabled={isCreating}
          className="w-full bg-brand-primary hover:bg-brand-primary-600 text-white mt-1"
        >
          {isCreating ? (
            <>
              <Loader2 size={15} className="animate-spin mr-2" />
              Création…
            </>
          ) : (
            'Créer le vote'
          )}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default EveningPlannerModal;
